import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { ExternalLink, HelpCircle, Users, Shield } from 'lucide-react';

// ─── Visits Line Chart ───────────────────────────────────────────────
function VisitsChart({ daily }) {
  const [hovered, setHovered] = useState(null);

  if (!daily || daily.length === 0) {
    return (
      <div className="flex items-center justify-center h-44 text-slate-400 dark:text-slate-500 text-sm">
        No visit data available yet
      </div>
    );
  }

  const W = 800, H = 220;
  const PAD = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartW = W - PAD.left - PAD.right;
  const chartH = H - PAD.top - PAD.bottom;
  const maxVal = Math.max(...daily.map(d => d.unique_visitors), 1);

  const pts = daily.map((d, i) => ({
    x: PAD.left + (i / Math.max(daily.length - 1, 1)) * chartW,
    y: PAD.top + chartH - (d.unique_visitors / maxVal) * chartH,
    ...d,
  }));

  // Smooth bezier curve
  let linePath = `M ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p = pts[i - 1], c = pts[i];
    const dx = (c.x - p.x) * 0.4;
    linePath += ` C ${p.x + dx} ${p.y}, ${c.x - dx} ${c.y}, ${c.x} ${c.y}`;
  }
  const areaPath = `${linePath} L ${pts[pts.length - 1].x} ${PAD.top + chartH} L ${pts[0].x} ${PAD.top + chartH} Z`;

  const yTicks = 4;
  const yLabels = Array.from({ length: yTicks + 1 }, (_, i) => ({
    val: Math.round((maxVal / yTicks) * (yTicks - i)),
    y: PAD.top + (i / yTicks) * chartH,
  }));

  const xLabelStep = Math.max(1, Math.ceil(daily.length / 6));
  const fmtDate = (s) => {
    try { return new Date(s + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' }); }
    catch { return s; }
  };

  return (
    <div className="relative select-none">
      <svg
        width="100%" viewBox={`0 0 ${W} ${H}`}
        className="w-full overflow-visible"
        onMouseLeave={() => setHovered(null)}
      >
        <defs>
          <linearGradient id="vGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FEAB3D" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#FEAB3D" stopOpacity="0" />
          </linearGradient>
          <clipPath id="vClip">
            <rect x={PAD.left} y={PAD.top} width={chartW} height={chartH} />
          </clipPath>
        </defs>

        {/* Grid */}
        {yLabels.map((t, i) => (
          <g key={i}>
            <line x1={PAD.left} y1={t.y} x2={PAD.left + chartW} y2={t.y}
              stroke="#e2e8f0" strokeWidth="1" strokeDasharray="4 4"
              className="dark:stroke-slate-700" />
            <text x={PAD.left - 8} y={t.y + 4} fontSize="11" textAnchor="end"
              fill="#94a3b8">{t.val.toLocaleString()}</text>
          </g>
        ))}

        {/* Hovered column */}
        {hovered && (
          <rect
            x={hovered.x - chartW / (daily.length * 2)}
            y={PAD.top} width={chartW / daily.length} height={chartH}
            fill="#FEAB3D" opacity="0.07" rx="2"
          />
        )}

        {/* Area + Line */}
        <path d={areaPath} fill="url(#vGrad)" clipPath="url(#vClip)" />
        <path d={linePath} stroke="#FEAB3D" strokeWidth="2.5" fill="none"
          clipPath="url(#vClip)" strokeLinejoin="round" strokeLinecap="round" />

        {/* Points + hover zones */}
        {pts.map((pt, i) => (
          <g key={i}>
            <rect
              x={pt.x - chartW / (daily.length * 2)} y={PAD.top}
              width={chartW / daily.length} height={chartH}
              fill="transparent" style={{ cursor: 'crosshair' }}
              onMouseEnter={() => setHovered(pt)}
            />
            <circle cx={pt.x} cy={pt.y}
              r={hovered?.date === pt.date ? 6 : 4}
              fill={hovered?.date === pt.date ? '#FEAB3D' : '#fff'}
              stroke="#FEAB3D" strokeWidth="2.5"
            />
          </g>
        ))}

        {/* X labels */}
        {daily.map((d, i) => {
          if (i % xLabelStep !== 0 && i !== daily.length - 1) return null;
          const x = PAD.left + (i / Math.max(daily.length - 1, 1)) * chartW;
          return (
            <text key={i} x={x} y={H - 8} fontSize="11"
              textAnchor="middle" fill="#94a3b8">{fmtDate(d.date)}</text>
          );
        })}
      </svg>

      {/* Tooltip */}
      {hovered && (
        <div
          className="absolute z-20 pointer-events-none"
          style={{
            left: `${(hovered.x / W) * 100}%`,
            top: `${((hovered.y - 10) / H) * 100}%`,
            transform: 'translate(-50%, -100%)',
          }}
        >
          <div className="bg-[#021426] text-white text-xs px-3 py-2 rounded-xl shadow-2xl border border-[#FEAB3D]/40 whitespace-nowrap">
            <p className="font-semibold text-[#FEAB3D] mb-0.5">{fmtDate(hovered.date)}</p>
            <p>{hovered.unique_visitors.toLocaleString()} unique visitors</p>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main Panel ───────────────────────────────────────────────────────
export default function VisitsAndIPsPanel({ projectId, dateRange }) {
  const [visitsData, setVisitsData] = useState(null);
  const [ipsData, setIpsData]     = useState(null);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!projectId) return;
    setLoading(true);
    Promise.all([
      axios.get(`/analytics/${projectId}/visits?days=${dateRange}`),
      axios.get(`/analytics/${projectId}/top-ips?days=${dateRange}&limit=20`),
    ])
      .then(([vRes, iRes]) => { setVisitsData(vRes.data); setIpsData(iRes.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [projectId, dateRange]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {[3, 2].map((span, i) => (
          <div key={i}
            className={`lg:col-span-${span} bg-white dark:bg-card rounded-2xl border border-slate-100 dark:border-border p-6 animate-pulse`}>
            <div className="h-4 bg-slate-100 dark:bg-slate-800 rounded w-32 mb-3" />
            <div className="h-44 bg-slate-50 dark:bg-slate-800/50 rounded-xl" />
          </div>
        ))}
      </div>
    );
  }

  const daily      = visitsData?.daily || [];
  const totalUniq  = visitsData?.total_unique_visitors || 0;
  const topIPs     = ipsData?.top_ips || [];
  const maxReqs    = topIPs[0]?.requests || 1;

  const fmtLastSeen = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
    catch { return ts; }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">

      {/* ── Visits Line Chart ─────────────────────────────────── */}
      <Card className="lg:col-span-3 bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none p-6 border border-slate-100 dark:border-border transition-colors">
        {/* Header row */}
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-1.5">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">Visits</h3>
              <div className="relative group">
                <HelpCircle className="w-4 h-4 text-slate-400 dark:text-slate-500 cursor-help" />
                <div className="absolute left-6 top-0 z-30 hidden group-hover:block w-56
                  bg-[#021426] text-white text-xs px-3 py-2 rounded-xl shadow-xl border border-[#FEAB3D]/20">
                  Shows unique IP addresses per day. Excludes identifiable bot traffic.
                </div>
              </div>
            </div>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Unique IP addresses · Bots excluded
            </p>
          </div>
          {/* Icon badge — matches dashboard style */}
          <div className="w-11 h-11 bg-[#FFF6E5] dark:bg-[#021426]/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5 text-[#021426] dark:text-[#FEAB3D]" />
          </div>
        </div>

        {/* Big number */}
        <p className="text-4xl font-bold text-slate-900 dark:text-white mb-4">
          {totalUniq.toLocaleString()}
          <span className="text-sm font-normal text-slate-500 dark:text-slate-400 ml-2">
            unique visitors
          </span>
        </p>

        {/* Chart */}
        <VisitsChart daily={daily} />

        {/* Legend */}
        <div className="mt-3 flex gap-2 items-center px-3 py-1.5 bg-slate-50 dark:bg-slate-800/50 rounded-lg w-fit">
          <span className="w-4 h-0.5 bg-[#FEAB3D] rounded inline-block" />
          <span className="text-xs text-slate-500 dark:text-slate-400">Unique visitors / day</span>
        </div>
      </Card>

      {/* ── Top Client IPs Table ──────────────────────────────── */}
      <Card className="lg:col-span-2 bg-white dark:bg-card rounded-2xl shadow-md dark:shadow-none p-6 border border-slate-100 dark:border-border transition-colors flex flex-col">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-1.5">
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">Top Client IPs</h3>
            <div className="relative group">
              <HelpCircle className="w-4 h-4 text-slate-400 dark:text-slate-500 cursor-help" />
              <div className="absolute left-6 top-0 z-30 hidden group-hover:block w-56
                bg-[#021426] text-white text-xs px-3 py-2 rounded-xl shadow-xl border border-[#FEAB3D]/20">
                IPs ranked by total requests in the selected period. Hover a row to look it up.
              </div>
            </div>
          </div>
          {/* Icon badge — matches dashboard style */}
          <div className="w-11 h-11 bg-[#FFF6E5] dark:bg-[#021426]/20 rounded-xl flex items-center justify-center flex-shrink-0">
            <Shield className="w-5 h-5 text-[#021426] dark:text-[#FEAB3D]" />
          </div>
        </div>

        {topIPs.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-slate-500 dark:text-slate-400 text-sm text-center py-8">
              No IP data available yet
            </p>
          </div>
        ) : (
          <div className="flex flex-col flex-1 min-h-0">
            {/* Column headers */}
            <div className="grid grid-cols-[1fr_auto] gap-3 px-3 pb-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-xs font-semibold text-[#FEAB3D] uppercase tracking-wider">IP</span>
              <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider text-right">
                Requests
              </span>
            </div>

            {/* Scrollable list */}
            <div className="flex-1 overflow-y-auto max-h-64 mt-1 space-y-0.5 custom-scrollbar">
              {topIPs.map((entry, idx) => {
                const barPct = Math.round((entry.requests / maxReqs) * 100);
                const isTop  = idx === 0;
                return (
                  <div
                    key={entry.ip}
                    className={`relative group grid grid-cols-[1fr_auto] gap-3 items-center px-3 py-2.5 rounded-xl transition-colors
                      ${isTop
                        ? 'bg-[#FFF6E5] dark:bg-[#FEAB3D]/10 border border-[#FEAB3D]/25'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800/50 border border-transparent'}`}
                  >
                    {/* Subtle bar fill for top entry */}
                    {isTop && (
                      <div
                        className="absolute inset-y-0 left-0 rounded-xl bg-[#FEAB3D]/8 dark:bg-[#FEAB3D]/5 pointer-events-none"
                        style={{ width: `${barPct}%` }}
                      />
                    )}

                    <div className="relative flex items-center gap-2 min-w-0">
                      {/* Rank number */}
                      <span className="text-xs text-slate-400 dark:text-slate-500 w-4 flex-shrink-0 tabular-nums">
                        {idx + 1}
                      </span>
                      {/* IP address */}
                      <span className={`font-mono text-sm truncate
                        ${isTop
                          ? 'font-semibold text-[#021426] dark:text-white'
                          : 'text-slate-700 dark:text-slate-300'}`}>
                        {entry.ip}
                      </span>
                      {/* External lookup link */}
                      <a
                        href={`https://ip-api.com/#${entry.ip}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Look up this IP"
                        className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                      >
                        <ExternalLink className="w-3 h-3 text-[#FEAB3D]" />
                      </a>
                      {/* Country badge */}
                      {entry.country && (
                        <span className="hidden sm:inline flex-shrink-0 text-xs text-slate-400 dark:text-slate-500">
                          {entry.country}
                        </span>
                      )}
                    </div>

                    {/* Request count */}
                    <span className={`relative text-sm font-bold tabular-nums text-right
                      ${isTop
                        ? 'text-[#021426] dark:text-[#FEAB3D]'
                        : 'text-slate-700 dark:text-slate-300'}`}>
                      {entry.requests.toLocaleString()}
                    </span>
                  </div>
                );
              })}
            </div>

            {/* Footer */}
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-3 pt-3 border-t border-slate-100 dark:border-slate-800">
              Showing top {topIPs.length} IPs · Last {dateRange} days
            </p>
          </div>
        )}
      </Card>
    </div>
  );
}
